import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Pencil, Trash2, QrCode } from 'lucide-react';
import { adminApi } from '../api';
import { useAuth } from '../AuthContext';
import { ENTITY_SCHEMAS } from '../schemas';
import { I18nInput, TextInput } from '../components/Field';
import { ImagePicker } from '../components/ImagePicker';
import { Modal } from '../components/Modal';
import { PageSettingsForm } from './PageEditor';

const disp = (v) => (v && typeof v === 'object' ? (v.it || Object.values(v)[0] || '') : v || '');

function EntityForm({ schema, initial, onChange, tenantId }) {
  const set = (k, val) => onChange({ ...initial, [k]: val });
  return (
    <div className="space-y-4">
      {schema.fields.map((f) => {
        const val = initial[f.key];
        if (f.type === 'i18n') return <I18nInput key={f.key} label={f.label} value={val} multiline={f.multiline} onChange={(v) => set(f.key, v)} />;
        if (f.type === 'image') return <ImagePicker key={f.key} label={f.label} value={val} tenantId={tenantId} onChange={(v) => set(f.key, v)} />;
        if (f.type === 'bool') return (
          <label key={f.key} className="flex items-center gap-2 text-sm text-paelbi-ink/80">
            <input type="checkbox" checked={!!val} onChange={(e) => set(f.key, e.target.checked)} /> {f.label}
          </label>
        );
        return <TextInput key={f.key} label={f.label} value={val} onChange={(v) => set(f.key, v)} />;
      })}
    </div>
  );
}

function EntityRow({ item, schema, index, onEdit, onRemove }) {
  return (
    <div data-testid={`entity-row-${item.id}`} className={`flex items-center gap-3 p-4 ${index > 0 ? 'border-t border-paelbi-line' : ''}`}>
      {item.data?.image && <img src={item.data.image} alt="" className="h-12 w-12 shrink-0 rounded-lg object-cover" />}
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium text-paelbi-ink">{disp(item.data?.[schema.titleField]) || '(senza titolo)'}</p>
        <p className="truncate text-xs text-paelbi-ink/50">{disp(item.data?.badge) || disp(item.data?.tag) || item.data?.cat || ''}</p>
      </div>
      {item.qr_token && <QrCode size={18} className="text-paelbi-brown" title="QR generato" />}
      <button type="button" data-testid={`entity-edit-${item.id}`} onClick={() => onEdit(item)} className="rounded-lg p-2 text-paelbi-ink/60 hover:bg-paelbi-bg"><Pencil size={16} /></button>
      <button type="button" data-testid={`entity-delete-${item.id}`} onClick={() => onRemove(item)} className="rounded-lg p-2 text-red-500 hover:bg-red-50"><Trash2 size={16} /></button>
    </div>
  );
}

function ItemsList({ items, schema, onEdit, onRemove }) {
  return (
    <div className="mt-6 overflow-hidden rounded-2xl border border-paelbi-line bg-white" data-testid="entity-items-list">
      {items.length === 0 && <p className="p-6 text-sm text-paelbi-ink/50">Nessun elemento. Aggiungine uno.</p>}
      {items.map((it, i) => (
        <EntityRow key={it.id} item={it} schema={schema} index={i} onEdit={onEdit} onRemove={onRemove} />
      ))}
    </div>
  );
}

function useEntityCrud(entityKey, scope, disabled) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // {id?, data}
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (disabled) return;
    setItems(await adminApi.listEntities(entityKey, scope));
  }, [entityKey, scope, disabled]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => setEditing({ data: {} });
  const openEdit = (it) => setEditing({ id: it.id, data: { ...it.data }, qr_token: it.qr_token });

  const save = async () => {
    setSaving(true);
    try {
      const body = { data: editing.data, active: true };
      if (editing.id) await adminApi.updateEntity(entityKey, editing.id, body, scope);
      else await adminApi.createEntity(entityKey, body, scope);
      setEditing(null);
      await load();
    } catch (e) {
      alert('Errore: ' + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const remove = async (it) => {
    if (!window.confirm('Eliminare questo elemento?')) return;
    await adminApi.deleteEntity(entityKey, it.id, scope);
    await load();
  };

  return { items, editing, setEditing, saving, openNew, openEdit, save, remove };
}

export function EntityManager({ entityKey }) {
  const { isSuper, tenantId } = useAuth();
  const schema = ENTITY_SCHEMAS[entityKey];
  const scope = isSuper ? tenantId : undefined;
  const [tab, setTab] = useState('items');
  const { items, editing, setEditing, saving, openNew, openEdit, save, remove } = useEntityCrud(entityKey, scope, isSuper && !tenantId);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl text-paelbi-ink sm:text-3xl">{schema.label}</h1>
          <p className="text-paelbi-ink/60">Gestisci elementi e impostazioni della pagina.</p>
        </div>
        {tab === 'items' && (
          <button type="button" data-testid="entity-new-button" onClick={openNew} className="inline-flex items-center gap-2 rounded-btn bg-paelbi-brown px-4 py-2.5 text-sm font-semibold text-white hover:bg-paelbi-brown-dark">
            <Plus size={16} /> Nuovo
          </button>
        )}
      </div>

      <div className="mt-6 flex gap-2 border-b border-paelbi-line">
        {[['items', 'Elementi'], ['page', 'Impostazioni pagina']].map(([k, l]) => (
          <button type="button" key={k} data-testid={`entity-tab-${k}`} onClick={() => setTab(k)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${tab === k ? 'border-paelbi-brown text-paelbi-brown' : 'border-transparent text-paelbi-ink/60'}`}>{l}</button>
        ))}
      </div>

      {tab === 'items' && <ItemsList items={items} schema={schema} onEdit={openEdit} onRemove={remove} />}

      {tab === 'page' && (
        <div className="mt-6">
          <PageSettingsForm pageKey={schema.pageKey} tenantId={scope} />
        </div>
      )}

      <Modal
        open={!!editing}
        onClose={() => setEditing(null)}
        title={editing?.id ? 'Modifica elemento' : 'Nuovo elemento'}
        wide
        footer={<>
          <button type="button" onClick={() => setEditing(null)} className="rounded-btn border border-paelbi-line px-4 py-2 text-sm">Annulla</button>
          <button type="button" data-testid="entity-save-button" onClick={save} disabled={saving} className="rounded-btn bg-paelbi-brown px-5 py-2 text-sm font-semibold text-white disabled:opacity-60">{saving ? 'Salvo...' : 'Salva'}</button>
        </>}
      >
        {editing && <EntityForm schema={schema} initial={editing.data} tenantId={scope} onChange={(d) => setEditing({ ...editing, data: d })} />}
        {editing?.qr_token && <p className="mt-4 text-xs text-paelbi-ink/50">QR token: <span className="font-mono">{editing.qr_token}</span></p>}
      </Modal>
    </div>
  );
}

export default EntityManager;
